import moment from 'moment/moment';
import momentTz from 'moment-timezone';
import {
  DELIVERY_PROVIDER_TYPE,
  DIFF_SECONDS_TO_READY,
  EVENT_ORDER_TYPE_SELECT,
  ORDER,
  ORDER_CHANNELS,
  ORDER_SOURCE,
  ORDER_STATUS,
  ORDER_TYPE
} from 'helpers/constants/orders_v2';
import {FEATURE_RESTRICTION_IDS} from 'helpers/constants/restrictions';
import {
  LARGE_DELIVERY_ITEMS_THRESHOLD,
  LARGE_DELIVERY_MAX_DRIVERS,
  LARGE_DELIVERY_MIN_DRIVERS,
  LARGE_DELIVERY_REASON,
  LARGE_DELIVERY_SUBTOTAL_THRESHOLD,
  LARGE_DELIVERY_TIP_CUSTOM
} from 'helpers/constants/largeDelivery';
import {toE164UsPhone} from 'helpers/functionUtils/helpFunctions';

const getCurrentLocationTime = (timezone) =>
  moment()
    .utc()
    .utcOffset(Number(timezone) || 0)
    .format();

export const checkIsFutureOrder = (order) => {
  return (
    order[ORDER.AHEAD_TIME] &&
    moment(`${order[ORDER.EXPIRED_TIME]}Z`).utcOffset(0).format() >
      getCurrentLocationTime(order?.Timezone)
  );
};

export const calculateDiffSeconds = (order) => {
  const tzMinutes = (Number(order?.Timezone) || 0) * 60;
  const currentOrderTime = moment(order[ORDER.DATE]).utcOffset(tzMinutes, true);
  const currentLocationTime = moment.utc().utcOffset(tzMinutes);

  return currentOrderTime.diff(currentLocationTime, 'seconds');
};

export const checkIsRefundOrder = (_order) => {
  const order = {..._order};

  // return (
  //   Array.isArray(order[ORDER.REFUND]) &&
  //   order[ORDER.REFUND]?.some((item) => item?.status !== 'Rejected')
  // );

  return Array.isArray(order[ORDER.REFUND]) && order[ORDER.REFUND].length > 0;
};

export const checkIsFullNotToSauceRefundOrder = (_order) => {
  const order = {..._order};
  return (
    Array.isArray(order[ORDER.REFUND]) &&
    !!order[ORDER.REFUND]?.find((item) => item.type === 'Full')
  );
};

export const checkIsPossibleToCancelDriver = (order) => {
  return (
    moment(`${order[ORDER.DATE]}Z`).utc().utcOffset(0).format() >
      getCurrentLocationTime(order?.Timezone) &&
    order[ORDER.SOURCE] === ORDER_SOURCE.SAUCE
  );
};

export const hasPickupReadyRequiredFeature = (serviceFeatures) =>
  serviceFeatures?.includes(FEATURE_RESTRICTION_IDS.PICKUP_READY_REQUIRED);

export const isPickupReadyRequiredByocOrder = (order, serviceFeatures) =>
  hasPickupReadyRequiredFeature(serviceFeatures) &&
  order[ORDER.ORDER_TYPE] === ORDER_TYPE.DELIVERY &&
  order[ORDER.DELIVERY]?.provider === DELIVERY_PROVIDER_TYPE.DISPATCH &&
  !checkIsFutureOrder(order) &&
  order[ORDER.SOURCE] !== ORDER_SOURCE.SAUCE;

export const checkIsPossibleToCancelByocDelivery = (order, serviceFeatures) => {
  if (!isPickupReadyRequiredByocOrder(order, serviceFeatures)) {
    return false;
  }

  return (
    order[ORDER.ORDER_STATUS] === ORDER_STATUS.READY ||
    Boolean(order[ORDER.DELIVERY]?.locationTrackingUrl)
  );
};

export const checkIsFullRefundOrder = (order) => {
  return (
    !!order[ORDER.REFUND] &&
    !!order[ORDER.REFUND]?.find((item) => item.type === 'Full')
  );
};

export const formattedOrderForBaD = (order) => {
  const coreOrderId = order[ORDER.OLD_ORDER_ID] || order[ORDER.ID] || null;
  const coreOrderType = order[ORDER.OLD_ORDER_ID] ? 'Sauce' : 'Byoc';

  return {
    userEmail: order[ORDER.CUSTOMER]?.email,
    userName: order[ORDER.CUSTOMER]?.fullName,
    userPhone: order[ORDER.CUSTOMER]?.phone,
    orderInfo: {
      total: order[ORDER.TOTALS]?.locationTotal,
      items: order[ORDER.CART]?.items,
      alcoholTax: order[ORDER.TAXES]?.alcoholTaxAmount,
      deliveryAddress: order[ORDER.DELIVERY]?.address?.formattedAddress,
      deliveryAddressDecode: {
        Address: {
          Number: order[ORDER.DELIVERY]?.address?.number,
          Street: order[ORDER.DELIVERY]?.address?.street,
          City: order[ORDER.DELIVERY]?.address?.city,
          State: order[ORDER.DELIVERY]?.address?.state,
          ZipCode: order[ORDER.DELIVERY]?.address?.zipCode
        },
        Сoordinates: {
          Latitude: order[ORDER.DELIVERY]?.address?.latitude,
          Longitude: order[ORDER.DELIVERY]?.address?.longitude
        }
      },
      deliveryAddressDetails: {
        Notes: order[ORDER.DELIVERY]?.address?.notes,
        Apt: order[ORDER.DELIVERY]?.address?.apt,
        Entrance: order[ORDER.DELIVERY]?.address?.entrance,
        Floor: order[ORDER.DELIVERY]?.address?.floor
      }
    },
    invoiceID: order[ORDER.INVOICE_ID] || null,
    id: coreOrderId,
    coreOrderType,
    orderStatus: order[ORDER.ORDER_STATUS],
    date: order[ORDER.DATE],
    orderSource: order[ORDER.SOURCE]
  };
};

export const checkIsBaDPOS = (order) => {
  return (
    order[ORDER.SOURCE] !== ORDER_SOURCE.SAUCE &&
    order[ORDER.ORDER_TYPE] === ORDER_TYPE.DELIVERY &&
    order[ORDER.DELIVERY]?.provider === DELIVERY_PROVIDER_TYPE.DISPATCH
  );
};

export const isStreamEventsThirdPartyDeliveryOrPickup = (order) =>
  order[ORDER.CHANNEL] === ORDER_CHANNELS.STREAM_EVENT &&
  (order[ORDER.DELIVERY]?.provider === DELIVERY_PROVIDER_TYPE.ORDER_PROVIDER ||
    (order[ORDER.ORDER_TYPE] === ORDER_TYPE.PICKUP &&
      order[ORDER.SOURCE] !== ORDER_SOURCE.SAUCE));

const hasStreamEventsReadyButtonFeature = (serviceFeatures) =>
  serviceFeatures?.includes(
    FEATURE_RESTRICTION_IDS.STREAM_EVENTS_READY_BUTTON_MARKETPLACE_DELIVERY
  );

const requiresManualPickupReady = (order, serviceFeatures) =>
  (checkIsBaDPOS(order) && hasPickupReadyRequiredFeature(serviceFeatures)) ||
  (isStreamEventsThirdPartyDeliveryOrPickup(order) &&
    hasStreamEventsReadyButtonFeature(serviceFeatures));

export const calculateOverdueAndFuture = (
  order,
  pickupReadyEnabled = false,
  serviceFeatures
) => {
  // eslint-disable-next-line prefer-const
  let {internalOrderStatus, overdueDisabled, Timezone} = order;

  if (
    [ORDER_STATUS.NEW, ORDER_STATUS.ACCEPTED].includes(internalOrderStatus) ||
    !internalOrderStatus
  ) {
    const currentOrderTime = moment(`${order[ORDER.DATE]}Z`)
      .utcOffset(0)
      .format();

    if (
      getCurrentLocationTime(Timezone) > currentOrderTime &&
      !overdueDisabled &&
      !requiresManualPickupReady(order, serviceFeatures)
    ) {
      internalOrderStatus = ORDER_STATUS.OVERDUE;
    }
    if (
      checkIsFutureOrder(order) &&
      order[ORDER.ORDER_STATUS] !== ORDER_STATUS.ACCEPTED
    ) {
      internalOrderStatus = ORDER_STATUS.FUTURE;
    }

    if (
      pickupReadyEnabled &&
      !checkIsFutureOrder(order) &&
      getCurrentLocationTime(Timezone) > currentOrderTime &&
      !requiresManualPickupReady(order, serviceFeatures)
    ) {
      internalOrderStatus = ORDER_STATUS.READY;
    }
  }

  return {...order, internalOrderStatus};
};

export const shouldShowByocTrackDelivery = (order, serviceFeatures) => {
  if (!checkIsBaDPOS(order)) {
    return false;
  }

  if (isPickupReadyRequiredByocOrder(order, serviceFeatures)) {
    return true;
  }

  return Boolean(order[ORDER.DELIVERY]?.locationTrackingUrl);
};

export const filterEventOrders = (orders, eventOrderTypeSelected) => {
  if (eventOrderTypeSelected === EVENT_ORDER_TYPE_SELECT.ALL) {
    return orders;
  }

  return orders.filter((order) => {
    if (eventOrderTypeSelected === EVENT_ORDER_TYPE_SELECT.DELIVERY) {
      return order[ORDER.ORDER_TYPE] === ORDER_TYPE.DELIVERY;
    }
    if (eventOrderTypeSelected === EVENT_ORDER_TYPE_SELECT.SAUCE) {
      return order[ORDER.SOURCE] === ORDER_SOURCE.SAUCE;
    }
    return false;
  });
};

export const getTotalItemsCount = (items) =>
  items?.reduce((sum, item) => sum + (item?.quantity || 0), 0) || 0;

export const getTotalItemsLabel = (items) => {
  const total = getTotalItemsCount(items);
  return `${total} ${total === 1 ? 'item' : 'items'}`;
};

export const isLargeDeliveryOrder = (order) => {
  if (order?.[ORDER.ORDER_TYPE] !== ORDER_TYPE.DELIVERY) {
    return false;
  }
  if (order[ORDER.DELIVERY]?.provider !== DELIVERY_PROVIDER_TYPE.DISPATCH) {
    return false;
  }

  const itemsCount = getTotalItemsCount(order[ORDER.CART]?.items);
  const subtotal = Number(order[ORDER.TOTALS]?.locationSubtotal || 0);

  return (
    itemsCount >= LARGE_DELIVERY_ITEMS_THRESHOLD ||
    subtotal >= LARGE_DELIVERY_SUBTOTAL_THRESHOLD
  );
};

export const dollarsToCents = (value) => Math.round(Number(value || 0) * 100);

export const centsToDollars = (value) => Number(value || 0) / 100;

export const getLargeDeliveryFee = (order) => {
  const fromBff = order?.largeDelivery?.deliveryPrice;
  if (fromBff != null && fromBff !== '') {
    const parsed = Number(fromBff);
    if (Number.isFinite(parsed)) {
      return centsToDollars(parsed);
    }
  }
  return Number(order?.[ORDER.FEES]?.deliveryFee || 0);
};

export const getLargeDeliveryDefaultTipAmount = (order) => {
  const fromBff = order?.largeDelivery?.defaultTip;
  if (fromBff == null || fromBff === '') {
    return null;
  }
  const parsed = Number(fromBff);
  if (!Number.isFinite(parsed)) {
    return null;
  }
  return centsToDollars(parsed);
};

export const calculateLargeDeliveryTotal = ({
  deliveryFee,
  additionalDrivers,
  tip,
  customTipAmount,
  subtotal
}) => {
  const fee = Number(deliveryFee || 0) * Number(additionalDrivers || 1);
  if (tip === LARGE_DELIVERY_TIP_CUSTOM) {
    return fee + Number(customTipAmount || 0);
  }
  if (!tip) {
    return fee;
  }
  return fee + Number(subtotal || 0) * (Number(tip) / 100);
};

export const getLargeDeliveryTipCents = ({tip, customTipAmount, subtotal}) => {
  if (tip === LARGE_DELIVERY_TIP_CUSTOM) {
    return dollarsToCents(customTipAmount);
  }
  if (!tip) {
    return 0;
  }
  // Same as BaD create: tipCents = subtotalDollars * percent
  return Number((Number(subtotal || 0) * Number(tip)).toFixed(0));
};

const mapBaDDropoffAddress = (address = {}) => ({
  stateCode: address.state || address.stateCode || '',
  city: address.city || '',
  streetName: address.street || address.streetName || '',
  streetNumber: address.number || address.streetNumber || '',
  zipCode: address.zipCode || address.zip || '',
  latitude: address.latitude,
  longitude: address.longitude,
  apt: address.apt || null,
  floor: address.floor || null,
  entrance: address.entrance || null,
  unit: address.unit || null,
  note: address.notes || address.note || null,
  countryCode: address.countryCode || 'US',
  formattedAddress: address.formattedAddress || ''
});

const mapBaDCustomer = (order) => {
  const customer = order[ORDER.CUSTOMER] || {};
  return {
    name: customer.fullName || '',
    phone: toE164UsPhone(customer.phone) || customer.phone || '',
    ...(customer.email && {email: customer.email})
  };
};

const mapBaDCartItems = (items = []) =>
  (items || []).map((item) => ({
    name: item.name || 'Item',
    quantity: item.quantity || 1,
    description: item.description || null
  }));

export const getBaDCoreOrderType = (order) =>
  order[ORDER.OLD_ORDER_ID] || order[ORDER.SOURCE] === ORDER_SOURCE.SAUCE
    ? 'Sauce'
    : 'Byoc';

export const getBaDCoreOrderId = (order) =>
  order[ORDER.OLD_ORDER_ID] || order[ORDER.ID] || null;

export const formatCourierNames = (names = []) => {
  const list = (names || []).filter(Boolean);
  if (!list.length) {
    return '';
  }
  if (list.length === 1) {
    return list[0];
  }
  if (list.length === 2) {
    return `${list[0]} and ${list[1]}`;
  }
  return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
};

const isCateringLocation = (location) =>
  !!(location?.IsCatering || location?.DeliveryOptions?.IsCatering);

const DEFAULT_LOCATION_TIMEZONE_ID = 'America/New_York';

const toUtcFromLocationWallClock = (wallClock, timezoneId) => {
  const tz = timezoneId || DEFAULT_LOCATION_TIMEZONE_ID;
  const naive = String(wallClock).substring(0, 19);
  return momentTz.tz(naive, 'YYYY-MM-DDTHH:mm:ss', tz).utc().toISOString();
};

export const buildLargeDeliveryPreCheckPayload = (order, location) => {
  const payload = {
    locationId: location?._id,
    customer: mapBaDCustomer(order),
    dropoff: {
      address: mapBaDDropoffAddress(order[ORDER.DELIVERY]?.address)
    },
    orderValue: dollarsToCents(order[ORDER.TOTALS]?.locationTotal),
    containsAlcohol: Boolean(order[ORDER.TAXES]?.alcoholTaxAmount),
    isCatering: isCateringLocation(location),
    items: mapBaDCartItems(order[ORDER.CART]?.items)
  };

  if (order[ORDER.INVOICE_ID]) {
    payload.coreInvoiceId = order[ORDER.INVOICE_ID];
  }

  if (order[ORDER.SOURCE] && order[ORDER.SOURCE] !== ORDER_SOURCE.SAUCE) {
    payload.externalOrderProviderName = order[ORDER.SOURCE];
  }

  if (checkIsFutureOrder(order) && order[ORDER.DATE]) {
    payload.dropoffTime = toUtcFromLocationWallClock(
      order[ORDER.DATE],
      location?.TimezoneId
    );
  } else {
    payload.foodPreparationTime = location?.PickupEtaMinutes || 20;
  }

  return payload;
};

export const buildLargeDeliveryCreatePayload = ({
  order,
  location,
  additionalDrivers,
  tipCents
}) => {
  const preCheck = buildLargeDeliveryPreCheckPayload(order, location);
  const coreOrderId = getBaDCoreOrderId(order);
  const drivers = Math.min(
    LARGE_DELIVERY_MAX_DRIVERS,
    Math.max(LARGE_DELIVERY_MIN_DRIVERS, Number(additionalDrivers || 1))
  );

  const payload = {
    locationId: preCheck.locationId,
    isV2: true,
    reason: LARGE_DELIVERY_REASON,
    amount_of_drivers: drivers,
    tip: tipCents,
    coreOrderType: getBaDCoreOrderType(order),
    customer: preCheck.customer,
    dropoff: preCheck.dropoff,
    cart: {
      subtotal: dollarsToCents(order[ORDER.TOTALS]?.locationTotal),
      containsAlcohol: preCheck.containsAlcohol,
      items: preCheck.items
    },
    isCatering: preCheck.isCatering
  };

  if (preCheck.foodPreparationTime != null) {
    payload.foodPreparationTime = preCheck.foodPreparationTime;
  }
  if (preCheck.dropoffTime) {
    payload.orderAheadTime = preCheck.dropoffTime;
  }
  if (coreOrderId) {
    payload.coreOrderId = coreOrderId;
  }
  if (preCheck.coreInvoiceId) {
    payload.coreInvoiceId = preCheck.coreInvoiceId;
  }
  if (preCheck.externalOrderProviderName) {
    payload.externalOrderProviderName = preCheck.externalOrderProviderName;
  }

  return payload;
};

export const PICKUP_READY_HIDDEN_ORDER_STATUSES = [
  ORDER_STATUS.READY,
  ORDER_STATUS.CANCELLED,
  ORDER_STATUS.COMPLETED
];

export const isOrderInPickupReadyHiddenStatus = (order) =>
  PICKUP_READY_HIDDEN_ORDER_STATUSES.includes(order[ORDER.ORDER_STATUS]);

const isPickupReadyHiddenForStreamEventsOrder = (order, serviceFeatures) =>
  isStreamEventsThirdPartyDeliveryOrPickup(order) &&
  !hasStreamEventsReadyButtonFeature(serviceFeatures);

export const checkIsReadyOrder = (
  order,
  pickupReadyEnabled,
  serviceFeatures
) => {
  const currentOrderTime = moment(`${order[ORDER.DATE]}Z`)
    .utcOffset(0)
    .format();

  return (
    (pickupReadyEnabled &&
      order[ORDER.ORDER_STATUS] !== ORDER_STATUS.CANCELLED &&
      order[ORDER.ORDER_STATUS] !== ORDER_STATUS.COMPLETED &&
      !checkIsFutureOrder(order) &&
      getCurrentLocationTime(order?.Timezone) > currentOrderTime &&
      !requiresManualPickupReady(order, serviceFeatures)) ||
    order[ORDER.ORDER_STATUS] === ORDER_STATUS.READY
  );
};

export const displayPickupReadyButton = (
  order,
  pickupReadyEnabled,
  serviceFeatures
) => {
  if (
    isOrderInPickupReadyHiddenStatus(order) ||
    isPickupReadyHiddenForStreamEventsOrder(order, serviceFeatures)
  ) {
    return false;
  }

  const status = order[ORDER.ORDER_STATUS];
  const provider = order[ORDER.DELIVERY]?.provider;

  const isDispatchAheadAlreadyReady =
    provider === DELIVERY_PROVIDER_TYPE.DISPATCH &&
    order[ORDER.AHEAD_TIME] != null &&
    status === ORDER_STATUS.READY;

  if (isPickupReadyRequiredByocOrder(order, serviceFeatures)) {
    return Boolean(!isDispatchAheadAlreadyReady);
  }

  if (
    isStreamEventsThirdPartyDeliveryOrPickup(order) &&
    hasStreamEventsReadyButtonFeature(serviceFeatures)
  ) {
    return Boolean(
      pickupReadyEnabled &&
        provider !== DELIVERY_PROVIDER_TYPE.LOCATION &&
        !checkIsFutureOrder(order)
    );
  }

  return Boolean(
    pickupReadyEnabled &&
      provider !== DELIVERY_PROVIDER_TYPE.LOCATION &&
      !checkIsFutureOrder(order) &&
      !isDispatchAheadAlreadyReady &&
      !checkIsReadyOrder(order, pickupReadyEnabled, serviceFeatures)
  );
};

export const isPickupReadyButtonDisabled = (
  order,
  pickupReadyEnabled,
  serviceFeatures
) => {
  if (
    isPickupReadyRequiredByocOrder(order, serviceFeatures) ||
    (isStreamEventsThirdPartyDeliveryOrPickup(order) &&
      hasStreamEventsReadyButtonFeature(serviceFeatures))
  ) {
    return false;
  }

  const diffSeconds = calculateDiffSeconds(order);

  return (
    pickupReadyEnabled &&
    ((order[ORDER.ORDER_TYPE] === ORDER_TYPE.DELIVERY &&
      !order[ORDER.DELIVERY]?.pickupReadyEnabled) ||
      (order[ORDER.DELIVERY]?.provider === DELIVERY_PROVIDER_TYPE.DISPATCH &&
        diffSeconds < DIFF_SECONDS_TO_READY) ||
      (order[ORDER.DELIVERY]?.provider === DELIVERY_PROVIDER_TYPE.DISPATCH &&
        order[ORDER.AHEAD_TIME] !== null &&
        order[ORDER.ORDER_STATUS] !== ORDER_STATUS.READY))
  );
};

/** GrubHub refund / price-adjustment request lifecycle (API `status` field). */
export const normalizeRefundAdjustStatus = (status) => {
  if (status == null || status === '') {
    return 'Confirmed';
  }
  const normalized = String(status).toLowerCase();
  if (normalized === 'pending') {
    return 'Pending';
  }
  if (normalized === 'rejected') {
    return 'Rejected';
  }
  return 'Confirmed';
};

export const grubHubBlocksFurtherRefundAdjust = (order) => {
  if (
    order[ORDER.SOURCE] !== ORDER_SOURCE.GRUBHUB &&
    order[ORDER.CHANNEL] !== ORDER_CHANNELS.GRUBHUB
  ) {
    return false;
  }
  const refunds = order[ORDER.REFUND] || [];
  const partialBlocks = refunds.some(
    (r) =>
      r?.type === 'Partial' &&
      ['Pending', 'Confirmed'].includes(normalizeRefundAdjustStatus(r?.status))
  );
  if (partialBlocks) {
    return true;
  }
  const adjustments = order[ORDER.PRICE_ADJUSTMENTS] || [];
  return adjustments.some((a) =>
    ['Pending', 'Confirmed'].includes(normalizeRefundAdjustStatus(a?.status))
  );
};

export const getGrubHubPendingRequestNotes = (order) => {
  if (
    order[ORDER.SOURCE] !== ORDER_SOURCE.GRUBHUB &&
    order[ORDER.CHANNEL] !== ORDER_CHANNELS.GRUBHUB
  ) {
    return [];
  }
  const notes = [];
  const refunds = order[ORDER.REFUND] || [];
  if (
    refunds.some(
      (r) =>
        r?.type === 'Partial' &&
        normalizeRefundAdjustStatus(r?.status) === 'Pending'
    )
  ) {
    notes.push(
      'Refund request has been submitted to GrubHub and is pending their confirmation.'
    );
  }
  const adjustments = order[ORDER.PRICE_ADJUSTMENTS] || [];
  if (
    adjustments.some(
      (a) => normalizeRefundAdjustStatus(a?.status) === 'Pending'
    )
  ) {
    notes.push(
      'Order adjustment request has been submitted to GrubHub and is pending their confirmation.'
    );
  }
  return notes;
};

export const getGrubHubRejectedRequestNotes = (order) => {
  if (
    order[ORDER.SOURCE] !== ORDER_SOURCE.GRUBHUB &&
    order[ORDER.CHANNEL] !== ORDER_CHANNELS.GRUBHUB
  ) {
    return [];
  }
  const notes = [];
  const refunds = order[ORDER.REFUND] || [];
  if (
    refunds.some(
      (r) =>
        r?.type === 'Partial' &&
        normalizeRefundAdjustStatus(r?.status) === 'Rejected'
    )
  ) {
    notes.push(
      'Refund request has been rejected by provider. Please contact your GrubHub account manager for details'
    );
  }
  const adjustments = order[ORDER.PRICE_ADJUSTMENTS] || [];
  if (
    adjustments.some(
      (a) => normalizeRefundAdjustStatus(a?.status) === 'Rejected'
    )
  ) {
    notes.push(
      'Order adjustment request has been rejected by provider. Please contact your GrubHub account manager for details'
    );
  }
  return notes;
};

/** Totals / bill: for GrubHub only show adjustments that are no longer pending. */
export const getBillablePriceAdjustmentsForTotals = (order) => {
  const list = order[ORDER.PRICE_ADJUSTMENTS] || [];
  if (
    order[ORDER.SOURCE] !== ORDER_SOURCE.GRUBHUB &&
    order[ORDER.CHANNEL] !== ORDER_CHANNELS.GRUBHUB
  ) {
    return list;
  }
  return list.filter(
    (a) => normalizeRefundAdjustStatus(a?.status) === 'Confirmed'
  );
};

export const getOrderRefundHistoryDisplayItems = (order) => {
  const refunds = order[ORDER.REFUND] || [];
  if (
    order[ORDER.SOURCE] !== ORDER_SOURCE.GRUBHUB &&
    order[ORDER.CHANNEL] !== ORDER_CHANNELS.GRUBHUB
  ) {
    return refunds;
  }
  return refunds.filter((item) => {
    if (item?.type !== 'Partial') {
      return true;
    }
    return normalizeRefundAdjustStatus(item?.status) === 'Confirmed';
  });
};

export const getConfirmedPriceAdjustmentsForOrderNote = (order) => {
  const list = order[ORDER.PRICE_ADJUSTMENTS] || [];
  if (
    order[ORDER.SOURCE] !== ORDER_SOURCE.GRUBHUB &&
    order[ORDER.CHANNEL] !== ORDER_CHANNELS.GRUBHUB
  ) {
    return list;
  }
  return list.filter(
    (a) => normalizeRefundAdjustStatus(a?.status) === 'Confirmed'
  );
};

export const shouldShowPickupReadyUi = (
  order,
  pickupReadyEnabled,
  serviceFeatures
) => {
  if (isOrderInPickupReadyHiddenStatus(order)) {
    return false;
  }

  return (
    (pickupReadyEnabled || hasPickupReadyRequiredFeature(serviceFeatures)) &&
    displayPickupReadyButton(order, pickupReadyEnabled, serviceFeatures)
  );
};

export const shouldShowProviderId = (externalId) =>
  typeof externalId === 'string' &&
  externalId.trim().length > 0 &&
  externalId.trim().toLowerCase() !== 'n/a';

export const getOrderIdDisplay = (order) => {
  if (order[ORDER.SOURCE] === ORDER_SOURCE.SAUCE) {
    return order[ORDER.INVOICE_ID] || '';
  }

  if (shouldShowProviderId(order[ORDER.EXTERNAL_ID])) {
    return order[ORDER.EXTERNAL_ID];
  }

  return '';
};

export const getOrderPaymentMethodLabel = (paymentMethod) => {
  if (!paymentMethod) {
    return '';
  }

  if (String(paymentMethod).trim().toLowerCase() === 'unknown') {
    return '';
  }

  return paymentMethod;
};

export const isEventsOrder = (order) =>
  order[ORDER.CHANNEL] === ORDER_CHANNELS.STREAM_EVENT;

export const isOtterOrder = (order) =>
  order[ORDER.CHANNEL] === ORDER_CHANNELS.OTTER;

export const isSpotOnOrder = (order) =>
  order[ORDER.CHANNEL] === ORDER_CHANNELS.SPOTON;

export const isEventsLikeOrder = (order) =>
  isEventsOrder(order) || isOtterOrder(order) || isSpotOnOrder(order);

// 3P source with dispatch as the delivery provider.
export const isByocOrder = (order) =>
  order[ORDER.SOURCE] !== ORDER_SOURCE.SAUCE &&
  order[ORDER.DELIVERY]?.provider === DELIVERY_PROVIDER_TYPE.DISPATCH;

export const shouldShowTipsAndTotal = (order, allowedServiceFeatures) =>
  (!isByocOrder(order) && !isEventsLikeOrder(order)) ||
  allowedServiceFeatures?.includes(FEATURE_RESTRICTION_IDS.SHOW_BYOC_TIP);

export const shouldShowOrderTotal = (order, allowedServiceFeatures) =>
  shouldShowTipsAndTotal(order, allowedServiceFeatures) &&
  !(isEventsLikeOrder(order) && isByocOrder(order));

export const isSauceDispatchOrder = (order) =>
  order[ORDER.SOURCE] === ORDER_SOURCE.SAUCE &&
  order[ORDER.DELIVERY]?.provider === DELIVERY_PROVIDER_TYPE.DISPATCH;

export const shouldShowOrderPrice = (
  amount,
  orderSource,
  useSalesPackage = false
) => {
  if (useSalesPackage) {
    return false;
  }

  if (orderSource === ORDER_SOURCE.OTTER && Number(amount) === 0) {
    return false;
  }

  return true;
};
